import { useEffect, useMemo, useState } from "react";
import {
  getComplaints,
  updateComplaint,
} from "../../../services/adminComplaintApi";
import "./complaints.css";

export default function AdminComplaintList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      setLoadError("");
      const res = await getComplaints();
      setData(res.data.complaints || []);
    } catch (err) {
      console.error(err);
      setLoadError("Unable to load complaints right now.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComplaints();
  }, []);

  const handleUpdate = async (id, status) => {
    try {
      await updateComplaint(id, { status });
      fetchComplaints();
    } catch (err) {
      console.error("Update error:", err.response?.data || err);
      alert("Update failed");
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return data.filter((c) => {
      const matchSearch = query
        ? [
            c.student_name,
            c.student_class,
            c.student_section,
            c.message,
            c.status,
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query))
        : true;

      const matchStatus = filter === "all" ? true : c.status === filter;

      return matchSearch && matchStatus;
    });
  }, [data, filter, search]);

  const counts = data.reduce(
    (summary, item) => ({
      total: summary.total + 1,
      pending: summary.pending + (item.status === "pending" ? 1 : 0),
      resolved: summary.resolved + (item.status === "resolved" ? 1 : 0),
      rejected: summary.rejected + (item.status === "rejected" ? 1 : 0),
    }),
    { total: 0, pending: 0, resolved: 0, rejected: 0 },
  );

  return (
    <div className="complaints-page">
      <div className="complaints-hero">
        <div className="complaints-hero-left">
          <span className="complaints-hero-icon">📢</span>
          <div>
            <h1>Student Complaints</h1>
            <p>{counts.total} complaints / {filtered.length} shown</p>
          </div>
        </div>
      </div>

      <div className="complaints-stats">
        <div className="complaints-stat-card">
          <span>Total</span>
          <strong>{counts.total}</strong>
        </div>
        <div className="complaints-stat-card">
          <span>Pending</span>
          <strong>{counts.pending}</strong>
        </div>
        <div className="complaints-stat-card">
          <span>Resolved</span>
          <strong>{counts.resolved}</strong>
        </div>
        <div className="complaints-stat-card">
          <span>Rejected</span>
          <strong>{counts.rejected}</strong>
        </div>
      </div>

      <div className="complaints-topbar">
        <div className="complaints-search">
          <span>Search</span>
          <input
            placeholder="Student, class, section, message"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="complaints-filter">
          <span>Status</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="complaints-state">
          <span className="complaints-loader"></span>
          <strong>Loading complaints</strong>
          <p>Collecting student messages and current statuses.</p>
        </div>
      ) : loadError ? (
        <div className="complaints-state complaints-error">
          <strong>{loadError}</strong>
          <p>Try again after checking the connection.</p>
          <button onClick={fetchComplaints}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="complaints-state">
          <strong>No complaints found</strong>
          <p>
            {data.length === 0
              ? "Complaints submitted by students will appear here."
              : "No records match the selected search or status."}
          </p>
          {data.length > 0 && (
            <button
              onClick={() => {
                setSearch("");
                setFilter("all");
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="complaints-grid">
          {filtered.map((c) => (
            <article key={c.id} className={`complaint-card ${c.status || "pending"}`}>
              <div className="complaint-card-header">
                <div>
                  <h3>{c.student_name || "Student"}</h3>
                  <p>
                    {c.student_class
                      ? `${c.student_class} - ${c.student_section || "-"}`
                      : "Class not assigned"}
                  </p>
                </div>

                <span className={`complaint-status ${c.status || "pending"}`}>
                  {c.status || "pending"}
                </span>
              </div>

              <p className="complaint-message">
                {c.message || "No message provided."}
              </p>

              {c.audio && (
                <div className="complaint-audio">
                  <audio controls>
                    <source src={c.audio} />
                  </audio>
                </div>
              )}

              {c.screenshot && (
                <div className="complaint-screenshot">
                  <p className="complaint-screenshot-label">📸 Screenshot</p>
                  <a href={c.screenshot} target="_blank" rel="noreferrer">
                    <img
                      src={c.screenshot}
                      alt="Complaint screenshot"
                      className="complaint-screenshot-img"
                    />
                  </a>
                </div>
              )}

              <div className="complaint-footer">
                <span>{c.created_at || "No date"}</span>

                {c.status === "pending" && (
                  <div className="complaint-actions">
                    <button
                      className="complaint-resolve"
                      onClick={() => handleUpdate(c.id, "resolved")}
                    >
                      Resolve
                    </button>

                    <button
                      className="complaint-reject"
                      onClick={() => handleUpdate(c.id, "rejected")}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
